/**
 * @file card.tsx
 *
 * Card component for MD3 Expressive.
 *
 * Divided into two functional aspects (structure modeled after Android Card.kt):
 * - **Static**      → Renders a plain `<div>`, purely informational with no interaction.
 * - **Interactive** → Powered by `<motion.button>` or `<motion.a>`, featuring Ripple effect and elevation animation on hover/press/drag.
 *
 * Elevation levels (0–5):
 * - Level 0 = "none" (Flat)
 * - Level 1 = box-shadow ~1dp (Slight elevation - Elevated rest)
 * - Level 2 = box-shadow ~2dp (Hover/Focus elevation)
 * - Level 3 = box-shadow ~6dp (Dragged state elevation)
 * - Level 4 = box-shadow ~8dp (Pickers/Popups elevation)
 * - Level 5 = box-shadow ~12dp (Modals/Dialogs elevation)
 *
 * @example
 * ```tsx
 * // Static card with flat elevation
 * <Card variant="elevated" elevation={0}>
 *   <CardContent>Flat elevated surface</CardContent>
 * </Card>
 *
 * // Interactive button card without ripple or hover effect
 * <Card variant="filled" onClick={() => alert('Clicked!')} disableRipple disableHoverEffect>
 *   <CardContent>Clickable without ripple</CardContent>
 * </Card>
 *
 * // Interactive link card with custom hover scale
 * <Card variant="outlined" href="/home" whileHover={{ scale: 1.02 }}>
 *   <CardContent>Scaled link card</CardContent>
 * </Card>
 * ```
 *
 * @see https://m3.material.io/components/cards/overview
 */

import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { domMax, type HTMLMotionProps, LazyMotion, m } from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import { Ripple, useRippleState } from "../../ripple";
import { type CardVariant, useCardElevation } from "./card-tokens";
import type { CardProps } from "./types";

// ─────────────────────────────────────────────────────────────────────────────
// CVA – Variant base classes (token-aligned with MD3)
// ─────────────────────────────────────────────────────────────────────────────
export const cardVariants = cva(
	"rounded-m3-lg flex flex-col relative overflow-hidden transition-all duration-200",
	{
		variants: {
			variant: {
				// ElevatedCardTokens.ContainerColor = SurfaceContainerLow
				elevated: "bg-m3-surface-container-low text-m3-on-surface",
				// FilledCardTokens.ContainerColor = SurfaceContainerHighest
				filled: "bg-m3-surface-container-highest text-m3-on-surface",
				// OutlinedCardTokens.ContainerColor = Surface, OutlineColor = OutlineVariant
				outlined:
					"bg-m3-surface text-m3-on-surface border border-m3-outline-variant",
			},
		},
		defaultVariants: { variant: "elevated" },
	},
);

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const CardImpl = React.forwardRef<HTMLElement, CardProps>(
	(
		{
			className,
			variant = "elevated",
			elevation,
			disableElevation = false,
			ripple = true,
			disableRipple = false,
			hoverEffect = true,
			disableHoverEffect = false,
			stateLayer = true,
			disableStateLayer = false,
			disabled = false,
			interactive = false,
			draggable = false,
			href,
			target,
			rel: relProp,
			onClick,
			onPointerDown: userOnPointerDown,
			whileHover,
			whileTap,
			whileFocus,
			whileDrag,
			transition,
			asChild = false,
			children,
			...props
		},
		ref,
	) => {
		const safeVariant = (variant || "elevated") as CardVariant;
		const isInteractive = !!onClick || !!href || interactive || asChild;
		const isHoverEnabled = hoverEffect !== false && !disableHoverEffect;
		const isRippleEnabled =
			ripple !== false && !disableRipple && isInteractive && !disabled;
		const isStateLayerEnabled =
			stateLayer !== false && !disableStateLayer && isInteractive && !disabled;

		const elevationProps = useCardElevation({
			variant: safeVariant,
			disabled,
			draggable,
			elevation,
			disableElevation,
			hoverEffect: isHoverEnabled,
		});

		const {
			ripples,
			onPointerDown: rippleOnPointerDown,
			removeRipple,
		} = useRippleState();

		const handlePointerDown = (event: React.PointerEvent<HTMLElement>) => {
			if (isRippleEnabled) {
				rippleOnPointerDown(event);
			}
			userOnPointerDown?.(
				event as unknown as React.PointerEvent<HTMLButtonElement>,
			);
		};

		const baseClass = cn(
			cardVariants({ variant: safeVariant }),
			// Disabled state:
			// - pointer-events-none → disable interactions completely
			// - opacity-[0.38] → MD3 DisabledContainerOpacity
			disabled && "pointer-events-none opacity-[0.38]",
			className,
		);

		// MD3 State Layer (Hover: 8%, Focus: 10%, Pressed: 10%, Dragged: 16%)
		// Applied to interactive elements via absolute inset ::before pseudo-element
		const interactiveClass = cn(
			// Remove default outline, use MD3 state overlay & elevation for focus
			"focus-visible:outline-none focus:outline-none group",
			// Base pseudo-element overlay layer (when stateLayer is enabled)
			isStateLayerEnabled && [
				"before:absolute before:inset-0 before:pointer-events-none before:bg-m3-on-surface before:opacity-0 before:transition-opacity before:duration-200",
				isHoverEnabled && "hover:before:opacity-[0.08]",
				"focus-visible:before:opacity-[0.10] active:before:opacity-[0.10]",
				draggable && "active:before:opacity-[0.16]",
			],
			// Outlined interactive card: smooth transition of border color to m3-outline on hover/focus/press
			safeVariant === "outlined" &&
				"transition-colors duration-200 hover:border-m3-outline focus-visible:border-m3-outline active:border-m3-outline",
		);

		// Merge user-supplied motion props with elevation motion presets
		const mergedWhileHover =
			elevationProps.whileHover || whileHover
				? {
						...elevationProps.whileHover,
						...(typeof whileHover === "object" ? whileHover : {}),
					}
				: undefined;

		const mergedWhileTap =
			elevationProps.whileTap || whileTap
				? {
						...elevationProps.whileTap,
						...(typeof whileTap === "object" ? whileTap : {}),
					}
				: undefined;

		const mergedWhileFocus =
			elevationProps.whileFocus || whileFocus
				? {
						...elevationProps.whileFocus,
						...(typeof whileFocus === "object" ? whileFocus : {}),
					}
				: undefined;

		const mergedWhileDrag =
			elevationProps.whileDrag || whileDrag
				? {
						...elevationProps.whileDrag,
						...(typeof whileDrag === "object" ? whileDrag : {}),
					}
				: undefined;

		const mergedTransition =
			elevationProps.transition || transition
				? {
						...elevationProps.transition,
						...(typeof transition === "object" ? transition : {}),
					}
				: undefined;

		// ── asChild Delegation ───────────────────────────────────────────
		if (asChild) {
			const child = React.Children.only(children) as React.ReactElement<{
				children?: React.ReactNode;
			}>;
			const innerContent = (
				<>
					{isRippleEnabled && (
						<Ripple ripples={ripples} onRippleDone={removeRipple} />
					)}
					{child.props.children}
				</>
			);

			return (
				<LazyMotion features={domMax} strict>
					<Slot
						ref={ref as React.Ref<HTMLElement>}
						className={cn(baseClass, interactiveClass)}
						aria-disabled={disabled ? true : undefined}
						tabIndex={disabled ? -1 : 0}
						onPointerDown={handlePointerDown}
						onClick={onClick}
						{...(props as React.HTMLAttributes<HTMLElement>)}
					>
						{React.cloneElement(child, { children: innerContent })}
					</Slot>
				</LazyMotion>
			);
		}

		// ── Static Card – no interaction ─────────────────────────────────
		if (!isInteractive) {
			return (
				<div
					ref={ref as React.Ref<HTMLDivElement>}
					className={baseClass}
					style={{
						boxShadow: elevationProps.animate.boxShadow,
						...((props as React.HTMLAttributes<HTMLDivElement>).style || {}),
					}}
					aria-disabled={disabled ? true : undefined}
					{...(props as React.HTMLAttributes<HTMLDivElement>)}
				>
					{children}
				</div>
			);
		}

		// ── Safe rel: automatically add "noreferrer" when target="_blank" ─
		const safeRel = href
			? (relProp ?? (target === "_blank" ? "noreferrer" : undefined))
			: undefined;

		// ── Link Card ────────────────────────────────────────────────────
		if (href) {
			return (
				<LazyMotion features={domMax} strict>
					<m.a
						ref={ref as React.Ref<HTMLAnchorElement>}
						href={disabled ? undefined : href}
						target={target}
						rel={safeRel}
						className={cn(baseClass, interactiveClass)}
						aria-disabled={disabled ? true : undefined}
						tabIndex={disabled ? -1 : 0}
						onPointerDown={handlePointerDown}
						animate={elevationProps.animate}
						whileHover={mergedWhileHover}
						whileTap={mergedWhileTap}
						whileFocus={mergedWhileFocus}
						whileDrag={mergedWhileDrag}
						transition={mergedTransition}
						{...(props as unknown as HTMLMotionProps<"a">)}
					>
						{isRippleEnabled && (
							<Ripple ripples={ripples} onRippleDone={removeRipple} />
						)}
						{children}
					</m.a>
				</LazyMotion>
			);
		}

		// ── Interactive Button Card ──────────────────────────────────────
		return (
			<LazyMotion features={domMax} strict>
				<m.button
					ref={ref as React.Ref<HTMLButtonElement>}
					type="button"
					disabled={disabled}
					onClick={onClick}
					className={cn(baseClass, interactiveClass)}
					aria-disabled={disabled ? true : undefined}
					tabIndex={disabled ? -1 : 0}
					onPointerDown={handlePointerDown}
					animate={elevationProps.animate}
					whileHover={mergedWhileHover}
					whileTap={mergedWhileTap}
					whileFocus={mergedWhileFocus}
					whileDrag={mergedWhileDrag}
					transition={mergedTransition}
					{...props}
				>
					{isRippleEnabled && (
						<Ripple ripples={ripples} onRippleDone={removeRipple} />
					)}
					{children}
				</m.button>
			</LazyMotion>
		);
	},
);
CardImpl.displayName = "Card";

export const Card = React.memo(CardImpl);
